import { ref, computed } from 'vue'
import axios from 'axios'

const token = ref(localStorage.getItem('admin_token') || null)
const isAuthenticated = computed(() => !!token.value)

export function useAuth() {
  const loading = ref(false)
  const error = ref(null)

  // Create axios instance with auth header
  const createAuthAxios = () => {
    return axios.create({
      headers: {
        'Authorization': token.value ? `Bearer ${token.value}` : '',
        'Content-Type': 'application/json'
      }
    })
  }

  // Login function
  const login = async (password) => {
    loading.value = true
    error.value = null

    try {
      const response = await axios.post('/api/auth-login', { password })

      if (response.data.success) {
        token.value = response.data.token
        localStorage.setItem('admin_token', response.data.token)

        // Store expiration time
        const expiresAt = Date.now() + (response.data.expiresIn * 1000)
        localStorage.setItem('admin_token_expires', expiresAt)

        return { success: true }
      } else {
        error.value = response.data.error || 'Login failed'
        return { success: false, error: error.value }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed. Please try again.'
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      loading.value = false
    }
  }

  // Logout function
  const logout = () => {
    token.value = null
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_token_expires')
  }

  // Verify token is still valid
  const verifyToken = async () => {
    if (!token.value) {
      return false
    }

    // Check expiration from localStorage first
    const expiresAt = localStorage.getItem('admin_token_expires')
    if (expiresAt && Date.now() > parseInt(expiresAt)) {
      logout()
      return false
    }

    try {
      const response = await axios.post('/api/auth-verify', {}, {
        headers: {
          'Authorization': `Bearer ${token.value}`
        }
      })

      return response.data.valid
    } catch (err) {
      // If token is invalid or expired, logout
      if (err.response?.status === 401) {
        logout()
      }
      return false
    }
  }

  // Check if token is expired
  const isTokenExpired = () => {
    const expiresAt = localStorage.getItem('admin_token_expires')
    if (!expiresAt) return true
    return Date.now() > parseInt(expiresAt)
  }

  // Get authenticated axios instance
  const getAuthAxios = () => {
    if (!token.value) {
      throw new Error('Not authenticated')
    }
    return createAuthAxios()
  }

  return {
    token,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    verifyToken,
    isTokenExpired,
    getAuthAxios
  }
}
